'use client';

import { c as _c } from "react/compiler-runtime";
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { cn } from '@morpho-org/ui-v6/utils';
import { Button } from '@repo/ui';
import { Close } from '@repo/ui/icons';
import { type ComponentPropsWithoutRef, forwardRef, Fragment, memo, type ReactNode, type SyntheticEvent } from 'react';
import type { ConsentType } from '@/helpers/consent.ts';
import { useIsMobile } from '@/hooks/useBreakpoints';
import { useConsentDialog } from '@/hooks/useConsentDialog.ts';
import HowItWorksBorrowAnimation from './animations/borrow/HowItWorksBorrowAnimation.tsx';
import HowItWorksEarnAnimation from './animations/earn/HowItWorksEarnAnimation.tsx';
import { useHowItWorksOverlay } from './useHowItWorksOverlay.ts';

// Rich links: underlined anchors that fade to primary text color on hover.
const richLinks = '[&_a]:underline [&_a]:[transition:color_120ms_linear] [&_a:hover]:text-foreground';
const Separator = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div role="separator" className="border-border [margin:32px_0] w-full border-t-[0.5px] [border-top-style:solid] max-md:[margin:24px_0]" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};

// Terms copy (text-2xs, secondary) with embedded rich links.
const Terms = t0 => {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("text-2xs text-secondary-foreground max-md:mt-xs", richLinks);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <div className={t1}>{children}</div>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const DisclaimerTerms = t0 => {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("text-2xs text-secondary-foreground mt-sm max-md:mt-xs", richLinks);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <div className={t1}>{children}</div>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
interface SelectedProps {
  selected: boolean;
  children: ReactNode;
}
const DesktopStep = t0 => {
  const $ = _c(10);
  let children;
  let props;
  let selected;
  if ($[0] !== t0) {
    ({
      selected,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
    $[3] = selected;
  } else {
    children = $[1];
    props = $[2];
    selected = $[3];
  }
  const t1 = selected ? "opacity-100" : "opacity-20";
  let t2;
  if ($[4] !== t1) {
    t2 = cn("flex cursor-pointer [transition:opacity_120ms_linear]", t1);
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== children || $[7] !== props || $[8] !== t2) {
    t3 = <div className={t2} {...props}>{children}</div>;
    $[6] = children;
    $[7] = props;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
const MobileStepButton = t0 => {
  const $ = _c(10);
  let children;
  let props;
  let selected;
  if ($[0] !== t0) {
    ({
      selected,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
    $[3] = selected;
  } else {
    children = $[1];
    props = $[2];
    selected = $[3];
  }
  const t1 = selected ? "text-foreground" : "text-secondary-foreground";
  let t2;
  if ($[4] !== t1) {
    t2 = cn("relative m-0 cursor-pointer appearance-none border-0 bg-transparent p-0 [font:inherit] [&>span]:text-[currentColor] [&::after]:absolute [&::after]:inset-x-[-12px] [&::after]:inset-y-[-10px] [&::after]:content-['']", t1);
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== children || $[7] !== props || $[8] !== t2) {
    t3 = <button type="button" className={t2} {...props}>{children}</button>;
    $[6] = children;
    $[7] = props;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
const MobileStepContent = forwardRef<HTMLDivElement, SelectedProps>((t0, ref) => {
  const $ = _c(6);
  const {
    selected,
    children
  } = t0;
  const t1 = selected ? "visible opacity-100" : "invisible opacity-0";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("gap-xs absolute top-0 right-0 left-0 flex flex-col justify-center text-left [transition:opacity_400ms_ease-in-out]", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children || $[3] !== ref || $[4] !== t2) {
    t3 = <div ref={ref} className={t2}>{children}</div>;
    $[2] = children;
    $[3] = ref;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
});
MobileStepContent.displayName = 'MobileStepContent';
export interface HowItWorksOverlayProps {
  type: ConsentType;
  title: string;
  steps: {
    title: string;
    description: string;
  }[];
  /**
   * Rendered inside a `<p>` — pass inline content only (text + `<a>`),
   * not block-level elements. HTML strings are rendered as literal text;
   * pass JSX to render links (XSS hardening — do not reintroduce
   * `dangerouslySetInnerHTML`).
   */
  terms: ReactNode;
}
const Disclaimer = <DisclaimerTerms>
    <p>
      Morpho Association is not a regulated entity. It is your responsibility to ascertain whether
      you are permitted to use the products and services provided according to applicable laws or
      regulations.
    </p>
  </DisclaimerTerms>;
const HowItWorksOverlay = t0 => {
  const $ = _c(45);
  const {
    type,
    title,
    steps,
    terms
  } = t0;
  const {
    toggleDialog
  } = useDialogContext();
  const isMobile = useIsMobile();
  const {
    consentStatus,
    onClickConfirm,
    onClickContinue
  } = useConsentDialog(type);
  let t1;
  if ($[0] !== steps) {
    t1 = {
      steps
    };
    $[0] = steps;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    animationContainer,
    animationStep,
    mediaBase,
    mobileStepsContainer,
    setAnimationStep,
    onTouchStep,
    preventDoubleClickToSelect,
    getMobileStepRef
  } = useHowItWorksOverlay(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = function renderActionButton(label, onClick) {
      return <div className="max-md:mt-sm mt-[10px] flex justify-end max-lg:justify-center"><Button type="button" className="max-lg:mt-xs" onClick={onClick}>{label}</Button></div>;
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const renderActionButton = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      WebkitTextSizeAdjust: "100%"
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== consentStatus || $[5] !== isMobile || $[6] !== onClickConfirm || $[7] !== terms || $[8] !== title) {
    t4 = isMobile && <><span className="text-title-xs text-foreground z-1 flex min-h-[30px] items-center justify-center px-[40px] text-center">{title}</span><Terms><p>{terms}</p></Terms>{!consentStatus && <>{Disclaimer}{renderActionButton("Proceed", onClickConfirm)}</>}<Separator /></>;
    $[4] = consentStatus;
    $[5] = isMobile;
    $[6] = onClickConfirm;
    $[7] = terms;
    $[8] = title;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      color: "white"
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== animationStep || $[12] !== mediaBase || $[13] !== type) {
    t6 = type === "earn" && <HowItWorksEarnAnimation animationStep={animationStep} base={mediaBase} />;
    $[11] = animationStep;
    $[12] = mediaBase;
    $[13] = type;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== animationStep || $[16] !== mediaBase || $[17] !== type) {
    t7 = type === "borrow" && <HowItWorksBorrowAnimation animationStep={animationStep} base={mediaBase} />;
    $[15] = animationStep;
    $[16] = mediaBase;
    $[17] = type;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== animationContainer || $[20] !== t6 || $[21] !== t7) {
    t8 = <div ref={animationContainer} className="relative w-[44%] max-w-[640px] shrink-0 grow max-lg:w-[46%] max-md:mb-[-30px] max-md:[height:calc(100dvh-240px)] max-md:[max-height:calc((100vw-20px)*1.12)] max-md:min-h-[300px] max-md:w-full max-md:max-w-none" style={t5}>{t6}{t7}</div>;
    $[19] = animationContainer;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== animationStep || $[24] !== consentStatus || $[25] !== getMobileStepRef || $[26] !== isMobile || $[27] !== mobileStepsContainer || $[28] !== onClickConfirm || $[29] !== onClickContinue || $[30] !== onTouchStep || $[31] !== preventDoubleClickToSelect || $[32] !== setAnimationStep || $[33] !== steps || $[34] !== terms || $[35] !== title) {
    t9 = <div className="max-md:pb-lg flex shrink flex-col justify-center">{isMobile ? <Fragment key="mobile"><div className="gap-lg z-2 flex flex-row flex-nowrap justify-center">{steps.map((_, index) => <MobileStepButton key={index} onClick={() => setAnimationStep(index)} selected={index === animationStep}><span className="text-md">{"0" + (index + 1)}</span></MobileStepButton>)}</div><div className="mt-lg relative" ref={mobileStepsContainer} onTouchStart={onTouchStep} onTouchEnd={onTouchStep}>{steps.map((step, index_0) => <MobileStepContent key={index_0} ref={getMobileStepRef(index_0)} selected={index_0 === animationStep}><h4><span className="text-md text-foreground">{step.title}</span></h4><p className="text-pretty"><span className="text-secondary-foreground text-xs">{step.description}</span></p></MobileStepContent>)}</div></Fragment> : <Fragment key="desktop"><span className="text-foreground text-lg">{title}</span><div className="gap-sm mt-lg flex flex-col">{steps.map((step_0, index_1) => <DesktopStep key={index_1} onClick={() => setAnimationStep(index_1)} selected={index_1 === animationStep}><span className="text-foreground w-[42px] shrink-0 text-sm [&_span]:mt-[2.5px] [&_span]:inline-block [&_span]:[transform:scale(0.95,1.05)]"><span>{"0" + (index_1 + 1)}</span></span><div className="gap-xs flex flex-col" onMouseDown={preventDoubleClickToSelect}><h4><span className="text-foreground text-lg">{step_0.title}</span></h4><p><span className="text-secondary-foreground text-xs">{step_0.description}</span></p></div></DesktopStep>)}</div><Separator /><Terms><p>{terms}</p></Terms>{Disclaimer}{consentStatus ? renderActionButton("Continue", onClickContinue) : renderActionButton("Proceed", onClickConfirm)}</Fragment>}</div>;
    $[23] = animationStep;
    $[24] = consentStatus;
    $[25] = getMobileStepRef;
    $[26] = isMobile;
    $[27] = mobileStepsContainer;
    $[28] = onClickConfirm;
    $[29] = onClickContinue;
    $[30] = onTouchStep;
    $[31] = preventDoubleClickToSelect;
    $[32] = setAnimationStep;
    $[33] = steps;
    $[34] = terms;
    $[35] = title;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  let t10;
  if ($[37] !== consentStatus || $[38] !== toggleDialog) {
    t10 = consentStatus && <Button type="button" className="absolute top-[30px] right-[30px] z-3 max-lg:top-[15px] max-lg:right-[15px]" variant="secondary" size="iconMd" aria-label="Close" onClick={() => toggleDialog(false)}><Close /></Button>;
    $[37] = consentStatus;
    $[38] = toggleDialog;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  let t11;
  if ($[40] !== t10 || $[41] !== t4 || $[42] !== t8 || $[43] !== t9) {
    t11 = <div className="bg-popover z-50 flex h-full w-full gap-[10px] p-0 max-md:max-w-[100vw] max-md:flex-col" style={t3}>{t4}{t8}{t9}{t10}</div>;
    $[40] = t10;
    $[41] = t4;
    $[42] = t8;
    $[43] = t9;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  return t11;
};
export default memo(HowItWorksOverlay);